import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import FloorPlanEditor from '@/components/FloorPlanEditor';
import { useToast } from "@/hooks/use-toast";
import Topbar from '@/components/Topbar';
import Sidebar from '@/components/Sidebar';
import ResetDialog from '@/components/ResetDialog';
import AuthDialog from '@/components/dialogs/AuthDialog';
import { LoginDialog } from '@/components/dialogs/LoginDialog';
import UnsavedChangesDialog from '@/components/dialogs/UnsavedChangesDialog';
import MobileAlertDialog from '@/components/dialogs/MobileAlertDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useFileActions } from '@/components/actions/FileActions';
import { useExportActions } from '@/components/actions/ExportActions';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { TutorialProvider } from '@/contexts/TutorialContext';
import TutorialDialog from '@/components/tutorial/TutorialDialog';
import TutorialButton from '@/components/tutorial/TutorialButton';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FloorPlanContent {
  nodes: any[];
  edges: any[];
}

const IndexPage = () => {
  const { isFullscreen, exitFullscreen } = useFullscreen();
  const [zoom, setZoom] = useState(1);
  const { toast } = useToast();
  const editorRef = useRef<any>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [currentFloorPlanName, setCurrentFloorPlanName] = useState<string>('');
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  const handleShowAuthDialog = useCallback(() => setShowAuthDialog(true), []);
  const handleShowLoginDialog = useCallback(() => setShowLoginDialog(true), []);

  const handleFloorPlanNameUpdate = useCallback((newName: string) => {
    setCurrentFloorPlanName(newName);
  }, []);

  const { handleSave, handleSaveAs, handleLoad } = useFileActions({
    editorRef,
    onAuthRequired: handleShowLoginDialog,
    isAuthenticated: !!user,
    onFloorPlanNameUpdate: handleFloorPlanNameUpdate,
  });

  const { exportToImage, exportToPDF, exportToJSON } = useExportActions({
    onAuthRequired: handleShowLoginDialog,
    isAuthenticated: !!user,
  });

  const handleUndo = useCallback(() => {
    if (!editorRef.current?.undo) return;
    editorRef.current.undo();
  }, []);

  const handleRedo = useCallback(() => {
    if (!editorRef.current?.redo) return;
    editorRef.current.redo();
  }, []);

  const handleRefresh = useCallback(() => {
    console.log('Handling refresh');
    if (editorRef.current?.resetState) {
      editorRef.current.resetState([], []);
      setCurrentFloorPlanName('');
      toast({
        title: "Plattegrond gereset",
        description: "De plattegrond is teruggezet naar een leeg canvas",
      });
    }
  }, [toast]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (editorRef.current?.hasUnsavedChanges?.()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigationAttempt = (action: () => void) => {
    if (editorRef.current?.hasUnsavedChanges?.()) {
      setPendingNavigation(() => action);
      setShowUnsavedDialog(true);
    } else {
      action();
    }
  };

  const handleSaveAndNavigate = async () => {
    try {
      await handleSave();
      if (pendingNavigation) {
        pendingNavigation();
      }
      setShowUnsavedDialog(false);
      setPendingNavigation(null);
    } catch (error) {
      console.error('Error saving before navigation:', error);
      toast({
        title: "Fout bij opslaan",
        description: "Er is een fout opgetreden bij het opslaan. Wil je toch doorgaan?",
        variant: "destructive",
      });
    }
  };

  const handleDiscardAndNavigate = () => {
    if (pendingNavigation) {
      pendingNavigation();
    }
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  // Function to get active floor plan name
  const getActiveFloorPlanName = useCallback(async () => {
    if (!user) return null;
    
    try {
      const { data: activePlan, error } = await supabase
        .from('floor_plans')
        .select('name')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching active floor plan:', error);
        return null;
      }

      return activePlan?.name || null;
    } catch (error) {
      console.error('Error in getActiveFloorPlanName:', error);
      return null;
    }
  }, [user]);

  // Main useEffect for loading floor plans
  useEffect(() => {
    const loadFloorPlan = async () => {
      // Wacht tot AuthContext klaar is met laden
      if (loading) {
        console.log('AuthContext is still loading, waiting...');
        return;
      }

      setIsLoading(true);
      const planId = searchParams.get('id');
      const isPreview = searchParams.get('preview') === 'true';
      setIsPreviewMode(isPreview);
      
      try {
        if (!planId) {
          console.log('No plan ID, trying to load saved state');
          setCurrentFloorPlanName('');
          if (editorRef.current?.loadSavedState) {
            const hasLoadedState = editorRef.current.loadSavedState();
            if (hasLoadedState) {
              console.log('Loaded state from localStorage');
              // Don't fetch active plan name here - will be handled by separate useEffect
            } else {
              console.log('No saved state found, initializing empty editor');
              editorRef.current.resetState([], []);
            }
          }
          setIsLoading(false);
          return;
        }

        const query = supabase
          .from('floor_plans')
          .select('content, name')
          .eq('id', planId);

        if (!isPreview && user) {
          query.eq('user_id', user.id);
        }

        console.log('Loading floor plan:', planId);
        const { data: plan, error } = await query.maybeSingle();

        if (error) {
          console.error('Database error:', error);
          throw error;
        }

        if (!plan && !isPreview) {
          toast({
            title: "Plattegrond niet gevonden",
            description: "De opgevraagde plattegrond kon niet worden gevonden",
            variant: "destructive",
          });
          setCurrentFloorPlanName('');
          if (editorRef.current?.resetState) {
            editorRef.current.resetState([], []);
          }
          return;
        }

        const content = plan?.content as unknown as FloorPlanContent;
        const planName = plan?.name || 'Zonder titel';
        setCurrentFloorPlanName(planName);
        console.log('Loaded content:', content, 'Plan name:', planName);

        if (!content || !Array.isArray(content.nodes) || !Array.isArray(content.edges)) {
          if (!isPreview) {
            toast({
              title: "Ongeldig formaat",
              description: "De plattegrond heeft een ongeldig formaat",
              variant: "destructive",
            });
          }
          if (editorRef.current?.resetState) {
            editorRef.current.resetState([], []);
          }
          return;
        }
        
        console.log('Resetting editor state with content');
        if (editorRef.current?.resetState) {
          editorRef.current.resetState(content.nodes, content.edges);
        }
        
      } catch (error: any) {
        console.error('Error loading floor plan:', error);
        setCurrentFloorPlanName('');
        if (!isPreview) {
          toast({
            title: "Fout bij laden",
            description: error.message || "Er is een fout opgetreden bij het laden van de plattegrond",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadFloorPlan();
  }, [searchParams, user, loading, toast]);

  // Separate useEffect to handle active floor plan name after login
  useEffect(() => {
    const fetchActiveFloorPlanName = async () => {
      // Only run when:
      // - AuthContext is not loading
      // - User is available
      // - No plan ID in URL (localStorage scenario)
      // - App is not currently loading
      if (loading || !user || searchParams.get('id') || isLoading) {
        return;
      }

      console.log('Checking for active floor plan name after auth state change');
      
      try {
        const activePlanName = await getActiveFloorPlanName();
        if (activePlanName) {
          setCurrentFloorPlanName(activePlanName);
          console.log('Set current floor plan name from active plan:', activePlanName);
        } else {
          console.log('No active floor plan found');
          setCurrentFloorPlanName('');
        }
      } catch (error) {
        console.error('Error fetching active floor plan name:', error);
      }
    };

    fetchActiveFloorPlanName();
  }, [user, loading, searchParams, isLoading, getActiveFloorPlanName]);

  const handleSaveWithUpdate = async () => {
    await handleSave();
    if (editorRef.current?.markAsSaved) {
      editorRef.current.markAsSaved();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, exitFullscreen]);

  return (
    <TutorialProvider>
      <ReactFlowProvider>
        <div className="h-screen flex flex-col bg-background">
          {!isFullscreen && (
            <Topbar
              editorRef={editorRef}
              onSave={handleSaveWithUpdate}
              onSaveAs={handleSaveAs}
              onLoad={(id) => handleNavigationAttempt(() => handleLoad(id))}
              onExportPDF={exportToPDF}
              onExportImage={exportToImage}
              onExportJSON={exportToJSON}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onReset={() => handleNavigationAttempt(() => setShowResetDialog(true))}
              onLogin={handleShowLoginDialog}
              hasUnsavedChanges={editorRef.current?.hasUnsavedChanges?.()}
              currentFloorPlanName={currentFloorPlanName}
            />
          )}
          
          <div className="flex-1 flex overflow-hidden">
            {!isFullscreen && <Sidebar />}
            <div className={`flex-1 relative ${isFullscreen ? 'absolute inset-0 z-50' : ''}`}>
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-muted-foreground">Laden...</div>
                </div>
              ) : (
                <>
                  {isFullscreen && (
                    <div className="absolute top-6 right-6 z-60">
                      <Button 
                        className="bg-white shadow-lg border-2 border-gray-500 h-16 w-16 rounded-full hover:bg-gray-100"
                        onClick={exitFullscreen}
                        title="Sluit volledige kaart"
                        aria-label="Sluit volledige kaart"
                      >
                        <X className="h-8 w-8 text-gray-900" />
                      </Button>
                    </div>
                  )}
                  <TutorialButton />
                  <FloorPlanEditor zoom={zoom} ref={editorRef} />
                </>
              )}
            </div>
          </div>

          <ResetDialog
            open={showResetDialog}
            onOpenChange={setShowResetDialog}
            onConfirm={handleRefresh}
          />

          <AuthDialog
            open={showAuthDialog}
            onOpenChange={setShowAuthDialog}
          />

          <LoginDialog
            open={showLoginDialog}
            onOpenChange={setShowLoginDialog}
          />

          <UnsavedChangesDialog
            open={showUnsavedDialog}
            onOpenChange={setShowUnsavedDialog}
            onSave={handleSaveAndNavigate}
            onDiscard={handleDiscardAndNavigate}
          />

          <MobileAlertDialog isMobile={isMobile} />
          <TutorialDialog />
        </div>
      </ReactFlowProvider>
    </TutorialProvider>
  );
};

export default IndexPage;
